import type { CompRef } from '@wix/document-services-types';
import {
  CssCustomProperties,
  CssCustomPropertyItem,
  CssProperties,
  CssPropertyItem,
  Data,
} from '@wix/component-protocol/schema';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
// eslint-disable-next-line import/no-extraneous-dependencies
import supportedTypesByPanels from '@wix/editor-elements-panel-service/dist/supported-types.json';
import { AbstractComponentsPlatformService } from '../AbstractComponentsPlatformService';

import { ComponentsDefinitionsPlatformService } from '../ComponentsDefinitionsPlatformService';
import { AUTOPANELS_DATA_TYPES_BLACKLIST } from '../../constants';
import { ManifestComponentExtensionType } from '../../stores';
import { ACTION_NAME_TO_DATA_TYPE, NativeActionsList } from '../../types';
import { type ActionCapabilityTarget } from './types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const DESIGN_PANEL_CSS_SUPPORTED_TYPES = new Set(
  supportedTypesByPanels.design.cssPropertyType,
);

const ACTION_TO_SUPPORTED_TYPES_MAP = new Map<
  keyof typeof NativeActionsList,
  Set<string>
>([
  [
    NativeActionsList.settings,
    new Set(supportedTypesByPanels.settings.dataType),
  ],
  [
    NativeActionsList.manageItems,
    new Set(supportedTypesByPanels.manage.dataType),
  ],
  [NativeActionsList.design, new Set(supportedTypesByPanels.design.dataType)],
]);

export class EditActionCapabilityResolver extends AbstractComponentsPlatformService {
  private readonly componentsDefinitionsPlatformService: ComponentsDefinitionsPlatformService;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.componentsDefinitionsPlatformService =
      new ComponentsDefinitionsPlatformService(...args);
  }

  private isDataItemSupported(
    dataItem: Data[string],
    supportedTypes: Set<string>,
  ): boolean {
    if (!dataItem?.dataType) {
      return false;
    }

    const isBaseTypeSupported = supportedTypes.has(dataItem.dataType);

    // Special handling for a11y with attribute filtering
    if (dataItem.dataType === 'a11y') {
      // No attributes specified in componentsDefinitionsPlatformService → check base type support
      if (!dataItem.a11y?.attributes?.length) {
        return isBaseTypeSupported;
      }

      // Attributes specified → check if any match the supported list
      return dataItem.a11y.attributes.some((attr) =>
        supportedTypes.has(`${dataItem.dataType}.attributes.${attr}`),
      );
    }

    return isBaseTypeSupported;
  }

  private getFilteredDataByPanelType(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    supportedTypes: Set<string>,
  ): Data[string][] {
    return Object.values(
      this.componentsDefinitionsPlatformService.getData(editorPointers) ?? {},
    ).filter((item): item is Data[string] =>
      this.isDataItemSupported(item, supportedTypes),
    );
  }

  private getTextDataItems(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
  ): Data[string][] {
    let data = Object.values(
      this.componentsDefinitionsPlatformService.getData(editorPointers) ?? {},
    );

    /**
     * TODO:we should allow multiselection for edit text action
     */
    if (Array.isArray(editorPointers) && editorPointers.length === 1) {
      const resolver = this.virtualElementProvider
        .buildVirtualElement({
          editorPointers,
        })
        .getAPI().resolver;

      const extensionType = resolver.getExtensionType();

      /**
       * TODO: we should not check OOI here, the OOI's componentsDefinitionsPlatformService in the store
       * should be already ready for any logical operations
       */
      if (extensionType === ManifestComponentExtensionType.OOI) {
        const dataWithInnerElementData =
          this.componentsDefinitionsPlatformService.getDataRecursively(
            editorPointers,
          );
        data = dataWithInnerElementData.flatMap((elementData) =>
          Object.values(elementData),
        );
      }
    }

    return data.filter(
      (item): item is Data[string] =>
        (item as Data[string])?.dataType === DataType.text,
    );
  }

  getDataItems(
    actionName: keyof typeof NativeActionsList,
    editorPointers: EditorPointer<CompRef>[],
  ): Data[string][] {
    if (actionName === NativeActionsList.settings) {
      const supportedTypes = ACTION_TO_SUPPORTED_TYPES_MAP.get(actionName)!;
      return Object.values(
        this.componentsDefinitionsPlatformService.getData(editorPointers) ?? {},
      ).filter(
        (item): item is Data[string] =>
          this.isDataItemSupported(item, supportedTypes) &&
          // @ts-expect-error DataType[] does not satisfy `undefined`
          !AUTOPANELS_DATA_TYPES_BLACKLIST.includes(item?.dataType),
      );
    }

    const supportedTypes = ACTION_TO_SUPPORTED_TYPES_MAP.get(actionName);
    if (supportedTypes) {
      return this.getFilteredDataByPanelType(editorPointers, supportedTypes);
    }

    if (actionName === NativeActionsList.manageMenu) {
      return Object.values(
        this.componentsDefinitionsPlatformService.getMenuData(editorPointers) ??
          {},
      );
    }

    if (
      actionName === NativeActionsList.editImage ||
      actionName === NativeActionsList.adjustImage ||
      actionName === NativeActionsList.filterImage
    ) {
      return Object.values(
        this.componentsDefinitionsPlatformService.getImageData(
          editorPointers,
        ) ?? {},
      ) as Data[string][];
    }

    if (actionName === NativeActionsList.cropImage) {
      const imageData =
        this.componentsDefinitionsPlatformService.getImageData(
          editorPointers,
        ) ?? {};
      const contentFill =
        this.componentsDefinitionsPlatformService.getContentFill(
          editorPointers,
        );
      const contentFillKey = contentFill?.dataItemKey;
      if (imageData[contentFillKey!]?.dataType !== DataType.image) {
        return [];
      }
      return Object.values(imageData) as Data[string][];
    }

    if (actionName === NativeActionsList.changeMedia) {
      return Object.values(
        this.componentsDefinitionsPlatformService.getMediaData(
          editorPointers,
        ) ?? {},
      ) as Data[string][];
    }

    if (actionName === NativeActionsList.editText) {
      const textDataItems = this.getTextDataItems(editorPointers);

      if (textDataItems.length > 0) {
        return textDataItems;
      }
    }

    const dataType = ACTION_NAME_TO_DATA_TYPE[actionName];
    if (dataType) {
      return Object.values(
        this.componentsDefinitionsPlatformService.getData(editorPointers) ?? {},
      ).filter(
        (item): item is Data[string] =>
          (item as Data[string])?.dataType === dataType,
      );
    }

    return [];
  }

  getStyleItems(editorPointers: EditorPointer<CompRef>[]): {
    cssProperties: CssProperties;
    cssCustomProperties: CssCustomProperties;
  } {
    const styleDefinitions =
      this.componentsDefinitionsPlatformService.getStyle(editorPointers);

    const cssProperties = styleDefinitions?.cssProperties
      ? Object.fromEntries(
          Object.entries(styleDefinitions.cssProperties).filter(([prop]) =>
            DESIGN_PANEL_CSS_SUPPORTED_TYPES.has(prop),
          ),
        )
      : {};

    const cssCustomProperties = styleDefinitions?.cssCustomProperties
      ? Object.fromEntries(
          Object.entries(styleDefinitions.cssCustomProperties).filter(
            ([, propDef]) =>
              propDef.cssPropertyType
                ? DESIGN_PANEL_CSS_SUPPORTED_TYPES.has(propDef.cssPropertyType)
                : false,
          ),
        )
      : {};

    return { cssProperties, cssCustomProperties };
  }

  hasDataItems(
    actionName: keyof typeof NativeActionsList,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean {
    return this.getDataItems(actionName, editorPointers).length > 0;
  }

  hasStyleItems(editorPointers: EditorPointer<CompRef>[]): boolean {
    const items = this.getStyleItems(editorPointers);

    return (
      Object.keys(items.cssProperties).length > 0 ||
      Object.keys(items.cssCustomProperties).length > 0
    );
  }

  private hasHideableStyles({
    cssProperties,
    cssCustomProperties,
  }: {
    cssProperties: Record<string, CssPropertyItem>;
    cssCustomProperties: Record<string, CssCustomPropertyItem>;
  }): boolean {
    const hideable =
      cssProperties.display?.display?.displayValues?.some(
        (v) => v === 'none',
      ) ?? false;

    const hasDisplayCssCustomProperties = Object.values(
      cssCustomProperties,
    ).some((value) => value.display?.displayValues?.some((v) => v === 'none'));

    return hideable || hasDisplayCssCustomProperties;
  }

  private getElementsActionManifestApi(
    target: ActionCapabilityTarget,
  ): NonNullable<ActionCapabilityTarget['manifestApi']> {
    if (target.manifestApi) {
      return target.manifestApi;
    }

    const node = this.virtualElementProvider.buildVirtualElement({
      editorPointers: target.editorPointers,
    });

    if (node.isRefElement() && target.editorPointers.length === 1) {
      return this.virtualElementProvider
        .buildVirtualElement({
          contextBuilder: (builder) => {
            builder.withManifest(target.editorPointers[0], node.getManifest());
          },
        })
        .getAPI();
    }

    return node.getAPI();
  }

  isActionable(
    actionName: keyof typeof NativeActionsList,
    target: ActionCapabilityTarget,
  ): boolean {
    if (actionName === NativeActionsList.design) {
      return (
        this.hasStyleItems(target.editorPointers) ||
        this.hasDataItems(actionName, target.editorPointers)
      );
    }

    if (actionName === NativeActionsList.elements) {
      const manifestApi = this.getElementsActionManifestApi(target);

      if (manifestApi.node.isRoot()) {
        return false;
      }

      return this.hasHideableStyles(manifestApi.resolver.getStyleDefinitions());
    }

    return this.hasDataItems(actionName, target.editorPointers);
  }
}
